import LiftoffMRAID from "./madlib/liftoff_mraid";
import Logger from "./madlib/logger";
import {
  CLICK_TRACKING_URL_TYPE_LIFTOFF_ENDPOINT,
  CLICKTHROUGH_URL_TYPE_LIFTOFF_ENDPOINT,
  TRACKING_METHOD_IMAGE,
  TRACKING_METHOD_BEACON,
  PLATFORM_ANDROID,
} from "./constants";
import * as Utils from "./frontendlib/utils";

const getLiftoffContainerRect = () => {
  // NOTE(Casey): 'liftoff-snippet' might not exist if the creative is not
  // fully initialized.
  const containerEl =
    document.getElementById("liftoff-snippet") || document.body;
  return containerEl.getBoundingClientRect();
};

export const getClickTrackingQueryParams = (allInteractionData) => {
  const { lastInteraction, cumulativeStats } = allInteractionData || {};

  const containerRect = getLiftoffContainerRect();

  const params = {
    // Unix timestamp of when the click is sent.
    ct: Date.now(),
    // Width of #liftoff-snippet in px.
    aw: containerRect.width.toFixed(),
    // Height of #liftoff-snippet in px.
    ah: containerRect.height.toFixed(),
    // Whether or not MRAID is supported for this impression, determined
    // empirically based on the presence/absence of the mraid object.
    m: LiftoffMRAID.hasSupport("mraid") ? 1 : 0,
    // Total duration, in ms, that the creative was viewable prior to the
    // click. This depends on the mraid "viewableChange" event, so its
    // accuracy depends on the exchange's implementation.
    tv: LiftoffMRAID.getViewableDurationMS(),
  };

  if (lastInteraction == null) {
    return params;
  }

  // NOTE(Casey): We see some rare events that do not have x/y coordinates.
  if (lastInteraction.startX != null) {
    // The horizontal position of the click in pixels, relative to the
    // upper-left corner of #liftoff-snippet.
    params.cx = (lastInteraction.startX - containerRect.left).toFixed();
  }

  if (lastInteraction.startY != null) {
    // The vertical position of the click in pixels, relative to the
    // upper-left corner of #liftoff-snippet.
    params.cy = (lastInteraction.startY - containerRect.top).toFixed();
  }

  if (lastInteraction.durationMS != null) {
    // Duration in milliseconds of the click, as measured by the difference
    // between timestamps on the start and end events (e.g. touchstart and
    // touchend).
    params.dur = lastInteraction.durationMS.toFixed();
  }

  Object.assign(params, {
    // The value of Event.isTrusted (see
    // https://developer.mozilla.org/en-US/docs/Web/API/Event/isTrusted).
    it: lastInteraction.isTrusted ? 1 : 0,
    // Maximum force of the interaction on a 0-100 scale. derived from
    // Touch.force (for TouchEvents), MouseEvent.webkitForce, or
    // PointerEvent.pressure.  This may be 0 or have some arbitrary value if
    // the pointer device/surface doesn't support force/pressure measurement.
    frc: Math.round(lastInteraction.maxForce * 100),
    // Maximum area of the screen with which the user interacted in px^2  (e.g.
    // the area of the screen covered by a finger during a click). May be 0 or
    // have some arbitrary value if the pointer device/surface doesn't support
    // area measurement.
    ar: lastInteraction.maxArea.toFixed(),
    // Path length traversed from the beginning to the end of the click, in px.
    d: lastInteraction.distance.toFixed(),
    // An enum describing the type of pointing device--Mouse, Touch, or other
    // (e.g. pen/stylus). See the constants defined in `InteractionMonitor`
    // and the corresponding definitions in rtb.proto.
    pt: lastInteraction.pointerType,
    // Total number of distinct interactions detected prior to and including the
    // click. "Distinct interactions" are bounded by start/end events or by
    // the pointing device leaving the creative.
    ic: cumulativeStats.interactionCount,
    // Total path length of all interactions.
    tid: cumulativeStats.totalDistance.toFixed(),
  });

  return params;
};

/**
 * Given a baseClickURL (a string), attaches the caller's query
 * parameters provided as an object, and returns a string.
 */
export const constructLiftoffClickURL = (baseClickURL, queryParams) => {
  return Utils.appendParamObjectToURL(baseClickURL, queryParams);
};

/**
 * Returns a Promise that resolves when the tracking pixel at url is loaded.
 */
const pingTrackingURLWithImage = (url) => {
  return new Promise((resolve) => {
    // NOTE(Austin): Image is a the best method here as far as we know because
    // it's a foolproof way to get a no-cors request and avoid any strange
    // behavior with XMLHttpRequest. One downside is that Image only tells us
    // when it's fully loaded, and technically we don't need to wait that long.
    // We technically only need to wait for the request to send (we don't need
    // to wait for the response).

    // NOTE(Casey): In some cases (e.g. SKAdNetwork impressions on some
    // exchanges), we "force" a direct clickthrough with a click-tracking URL
    // that redirects to the MMP and app store. We still use an image tag to
    // ping Usher and the MMP, but it will generate an error. We still want
    // to click through in this case.

    // TODO(Casey): Consider alternatives to this (e.g. using an iframe).

    const img = new Image();
    img.onload = () => resolve(url);

    // TODO(Austin): Investigate if there are errors here that should be
    // rejected.
    img.onerror = () => resolve(url);
    img.src = url;
  }).catch((e) => {
    Logger.recordLabeledError("clicktracking_img_error", e || {});
  });
};

/**
 * pingTrackingURLWithBeacon sends a ping using the beacon API,
 * which guarantees that the url will be reached before the page is disposed.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API
 */
const pingTrackingURLWithBeacon = (url) => {
  return new Promise((resolve, reject) => {
    if (navigator.sendBeacon(url)) {
      resolve();
    } else {
      reject();
    }
  });
};

/**
 * NOTE(Casey): When the return_js=true query param is attached to an Usher URL,
 * Usher will return a JavaScript file containing
 * '_loOpen("{{HTTP_REDIRECT_URL}}")'. For unknown reasons, this
 * method for fetching the redirect URL works better than fetching it via XHR,
 * possibly due to CORS restrictions. See
 * https://docs.google.com/document/d/1nzlGVs9HNSY_Wd29UzoZSx1luZIwufl6yiQ8AXol7cU/
 */
const clickthroughWithScriptTag = ({
  clickthroughURL,
  platform,
  exchange,
  abTests = [],
  onlyMraid = false,
}) => {
  window._loOpen = (clickURL) =>
    // We assume the url returned is never a liftoff url.
    LiftoffMRAID.open(clickURL, {
      platform,
      exchange,
      abTests,
      isLiftoffURL: false,
      onlyMraid,
    });
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.onload = resolve;
    script.onerror = (e) => {
      Logger.recordLabeledError("click_script_error", e || {});
    };
    script.src = Utils.appendParamObjectToURL(clickthroughURL, {
      return_js: true,
    });
    document.body.appendChild(script);
  }).catch((e) => {
    Logger.recordLabeledError("clickthrough_error", e || {});
  });
};

/**
 * Returns true if clickTrackingURL (an object like { url, type }) is a Liftoff
 * click tracking URL.
 */
const isLiftoffClickTrackingURL = (clickTrackingURL) => {
  return clickTrackingURL.type === CLICK_TRACKING_URL_TYPE_LIFTOFF_ENDPOINT;
};

/**
 * Prepares and pings a clickTrackingURL (an object like { url, type }); common
 * Liftoff query parameters and the caller's queryParams are attached only to
 * Liftoff clickTrackingURLs.
 */
const pingClickTrackingURL = (clickTrackingURL, queryParams, abTests = []) => {
  const getClickTrackingURLString = (trackingMethod) => {
    // NOTE(Austin): Right now we should only have "liftoff" click tracking
    // URLs (i.e. http://click.liftoff.io/v1/campaign_click/... ).
    return isLiftoffClickTrackingURL(clickTrackingURL)
      ? constructLiftoffClickURL(clickTrackingURL.url, {
          ...queryParams,
          tracking_method: trackingMethod,
        })
      : clickTrackingURL.url;
  };

  // The waterfall of click tracking methods to try (in priority order).
  // Each object in the array should contain a map with the following
  // keys:
  // - method: The tracking method integer, as described in rtb.proto
  //           TrackingMethod.
  // - canUse: Whether the method should be tried.
  // - pingFn: The function to ping that takes a URL string and returns a
  //           promise.
  const clickTrackingWaterfall = [
    {
      method: TRACKING_METHOD_BEACON,
      canUse:
        abTests != null &&
        abTests.includes("madlib-beacon-ping-experiment") &&
        isLiftoffClickTrackingURL(clickTrackingURL) &&
        navigator.sendBeacon != null,
      pingFn: pingTrackingURLWithBeacon,
    },
    {
      method: TRACKING_METHOD_IMAGE,
      canUse: true,
      pingFn: pingTrackingURLWithImage,
    },
  ];

  // Chain all promises to try each click tracking method. If the
  // method at the previous index returns a rejected promise,
  // we try the next; otherwise we return. If we reach the end without
  // any methods succeeding, this method returns a rejected promise.
  return clickTrackingWaterfall
    .filter(({ canUse }) => canUse)
    .reduce(
      (prevPromiseFn, nextWaterfallOption) => () => {
        const { method, pingFn } = nextWaterfallOption;
        const urlString = getClickTrackingURLString(method);
        return prevPromiseFn().catch(() => pingFn(urlString));
      },
      () => Promise.reject()
    )();
};

const handleClickTracking = (clickTrackingURLs, queryParams, abTests = []) => {
  return new Promise((resolve) => {
    const pingCompletePromises = clickTrackingURLs.map((clickTrackingURL) => {
      return pingClickTrackingURL(clickTrackingURL, queryParams, abTests);
    });

    Promise.all(pingCompletePromises).then(resolve);
  }).catch((e) => {
    Logger.recordLabeledError("clicktracking_error", e || {});
  });
};

const isLiftoffClickthroughURL = (clickthroughURL) => {
  return clickthroughURL.type === CLICKTHROUGH_URL_TYPE_LIFTOFF_ENDPOINT;
};

/**
 * Opens a clickthrough URL (object like { url, type }) using the best method
 * for the given context (platform, isReengagement).
 */
const handleClickthrough = ({
  clickthroughURL,
  queryParams,
  exchange,
  platform,
  isReengagement,
  abTests = [],
}) => {
  /**
   * NOTE(oscar): As of it 2022-10-31, we only want to use MRAID for iOS POD.
   * The main reason for this is because we want to guarantee that:
   *
   * 1. SKStoreProductViewController (i.e. App Store overlay) is used. This
   * assumes mraid is needed for this.
   * 2. The click waterfall does not also open the App Store. This is an issue
   * we've seen with AppLovin, but it may affect other exchanges.
   */
  const isAutoclick = !!queryParams.pv;

  return new Promise((resolve) => {
    const isLiftoffURL = isLiftoffClickthroughURL(clickthroughURL);
    const urlStr = isLiftoffURL
      ? constructLiftoffClickURL(clickthroughURL.url, queryParams)
      : clickthroughURL.url;

    if (queryParams.mac || queryParams.dap) {
      // The method name here is pingClickTrackingURL we do ping clickthrough
      // URL here as this is a background click.
      pingClickTrackingURL(clickthroughURL, queryParams, abTests);
    } else {
      /**
       * NOTE(Casey): On Android, script tag clicks perform better than redirect
       * clicks, while on iOS the opposite is true. However, for reengagement on
       * iOS, trackers may use "universal links", which are special web pages that
       * deeplink to an app or redirect to the app store. These links historically
       * have not worked with redirects, so we fetch the link and send the user
       * directly.
       * NOTE(Polly): We append a suffix to certain fyber clickthrough urls when
       * the native browser should be opened upon a click. We should not use the
       * script tag when the "FYBER_OPEN_BROWSER" suffix is present in the url.
       * TODO(Casey): Test if we can now redirect to universal links on iOS
       * (we have been told that this may have been fixed).
       */
      if (
        isLiftoffURL &&
        (platform === PLATFORM_ANDROID || isReengagement) &&
        !urlStr.includes("FYBER_OPEN_BROWSER")
      ) {
        return clickthroughWithScriptTag({
          clickthroughURL: urlStr,
          platform,
          exchange,
          abTests,
        });
      }

      LiftoffMRAID.open(urlStr, {
        exchange,
        platform,
        abTests,
        isLiftoffURL,
        isAutoclick,
      });
    }

    resolve();
  }).catch((e) => {
    Logger.recordLabeledError("clickthrough_error", e || {});
  });
};

/**
 * Given clickConfig, an object like:
 *
 *   {
 *     clickthroughURL: { url, type },
 *     clickTrackingURLs: [{ url, type }, { url, type }, ...],
 *   }
 *
 * prepares all URLs as appropriate based on type, pings tracking URLs, and
 * opens the click URL using the optimal method for a given context
 * (isReengagement, platform).
 */
export const handleClick = ({
  clickConfig,
  queryParams,
  isReengagement,
  platform,
  exchange,
  // NOTE(Austin): The below parameters are being used temporarily
  // for different AB tests.
  abTests = null,
}) => {
  // NOTE(oscar): Some exchanges wrap our URLs in order to track their
  // metrics. We perform an asynchronous ping to this link while redirecting
  // users to our actual link to reduce latency.

  // TODO(Casey): Determine if we can move the exchange ping URL into
  // clickTrackingURLs and handle along with our own tracking URLs.
  const exchangePingElem = document.getElementById("exchange-ping-url");

  if (exchangePingElem != null && exchangePingElem.href != null) {
    Utils.ping(exchangePingElem.href);
  }

  const { clickthroughURL, clickTrackingURLs } = clickConfig;

  const clickthroughParams = {
    clickthroughURL,
    queryParams,
    platform,
    isReengagement,
    exchange,
    abTests,
  };
  /**
   * Handles both clicktracking and clickthrough.
   *
   * NOTE(Austin): We are testing waiting for the exchange ping to see if there
   * is some race condition.
   *
   * TODO(Austin): Consolidate rejected promises and log errors here.
   */
  const doFullClick = () => {
    /** Product View clicks are not tracked. */
    let promise = Promise.resolve();
    if (!queryParams.pv) {
      promise = handleClickTracking(clickTrackingURLs, queryParams, abTests);
    }

    return promise.then(() => handleClickthrough(clickthroughParams));
  };

  return doFullClick();
};

export const _forUnitTestingOnly = {
  pingTrackingURLWithImage,
  isLiftoffClickTrackingURL,
  pingClickTrackingURL,
  handleClickTracking,
  isLiftoffClickthroughURL,
  clickthroughWithScriptTag,
  handleClickthrough,
  // We re-export LiftoffMRAID for unit testing to ensure that tests and the
  // functions in this file refer to the identical LiftoffMRAID object.
  LiftoffMRAID,
};



// WEBPACK FOOTER //
// ./src/js/click_handling.js